import type { DateTime } from "luxon";

/** Zero-pad a number to two digits. Used for `HH:MM`-style time keys. */
const padTwo = (n: number): string => n.toString().padStart(2, "0");

/** Format `(hour, minute)` as `HH:MM` (zero-padded, 24-hour). Used by ScheduleRow `shiftKey` fields. */
export const formatHourMinute = (hour: number, minute: number): string => `${padTwo(hour)}:${padTwo(minute)}`;

/**
 * Format a DateTime as a short 12-hour clock value, dropping `:00` minutes.
 * Examples: `7:00 AM` → `7 AM`, `3:30 PM` → `3:30 PM`.
 *
 * When `timezone` is provided the DateTime is normalized to that zone before formatting; pass the
 * department's timezone to get the operator-local display. Pass `undefined` to format in whatever
 * zone the DateTime already carries.
 */
export const formatTimeTo12Hour = (time: DateTime, timezone: string | undefined): string => {
	const zoned = timezone ? time.setZone(timezone) : time;
	return zoned.toFormat("h:mm a").replace(":00", "");
};

/**
 * Compose a row's shift-time display label as `"<start> - <end>"`.
 *
 * Used as the horizontal-axis display value when `columnGroupingBy === "shiftTime"`
 * (mapping §8.2) and as the cell value for the Shift Time column (mapping §5.2).
 */
export const formatShiftTimeRange = (start: DateTime, end: DateTime, timezone: string): string => {
	return `${formatTimeTo12Hour(start, timezone)} - ${formatTimeTo12Hour(end, timezone)}`;
};

/**
 * The canonical exact-window key for shift-time grouping.
 *
 * Format: `"HH:MM-HH:MM"` (zero-padded). This is the §6.2 group key for
 * `DailyPrintoutGroupingOption.START_TIME` before §6.3 popularity-anchored canonicalization.
 */
export const shiftWindowKey = (start: DateTime, end: DateTime): string => {
	return `${formatHourMinute(start.hour, start.minute)}-${formatHourMinute(end.hour, end.minute)}`;
};

/**
 * Convert a `shiftWindowKey` (`"HH:MM-HH:MM"`) back to a compact AM/PM display, matching legacy
 * `groupingKeyToDisplayName` for `START_TIME` (e.g. `"06:00-18:00"` → `"6a-6p"`,
 * `"06:30-14:30"` → `"6:30a-2:30p"`). Used for sheet-tab names when `separateSheetsBy === START_TIME`.
 *
 * Falls through unchanged if the key doesn't parse as a window (e.g. sentinel keys like "Other").
 */
export const shiftWindowKeyToDisplayName = (key: string): string => {
	const match = key.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
	if (!match) return key;
	const [, sH, sM, eH, eM] = match;
	const fmt = (hStr: string, mStr: string): string => {
		const h = Number(hStr);
		const m = Number(mStr);
		const ampm = h >= 12 ? "p" : "a";
		const displayH = h % 12 === 0 ? 12 : h % 12;
		return m === 0 ? `${displayH}${ampm}` : `${displayH}:${padTwo(m)}${ampm}`;
	};
	return `${fmt(sH, sM)}-${fmt(eH, eM)}`;
};

/**
 * The "merge-similar" prefix key: exact start time + floor-to-hour end time (mapping §8.2).
 *
 * Two shifts share this key iff they have the same start time (to the minute) and the same
 * end hour (their end minutes may differ). Used by both the horizontal-axis alias map (Case A)
 * and the anchor-row alias map (Case B).
 */
export const shiftSimilarityKey = (start: DateTime, end: DateTime): string => {
	return `${start.toISO()}~${end.startOf("hour").toISO()}`;
};
